import { Trash2 } from "lucide-react";

/**
 * Newest-first list of saved reflections with edit/delete, shared by both
 * `SeasonReflectionSection` and `MatchReflectionsSection` (`../pages/
 * Reflections.jsx`). Extracted to its own file (#965 review round 2, F2/F3)
 * so a real render test can mount it without pulling in `Reflections.jsx`'s
 * own heavy import graph (the kit barrel's `TopBar` needs a Router; `sonner`,
 * `../lib/api`, `../lib/voice` are only needed for the page's own sections).
 *
 * `kit` (#965) — flag-on callers pass their own `isNewUi` explicitly (never
 * inferred in here) so this list uses the kit's own tokens instead of raw
 * Tailwind slate. Round 2 (F1/F2): the kit branch is a FULL className swap,
 * never a slate class plus an inline-style colour override — an inline
 * `style` always wins over a `:hover` class at equal specificity, which is
 * exactly why the delete icon's hover never fired in round 1 (measured
 * `rgb(148, 163, 184)` — the resting colour — while genuinely `:hover`).
 * `kit=false` (the default, and every flag-off call) keeps today's classes
 * completely unchanged, with no `style` attribute at all.
 *
 * Round 2 (R1) — the delete icon's resting colour is `--nw-ink-muted`
 * (#64748B, 4.76:1 on white), not `--nw-ink-faint` (#94A3B8, 2.56:1):
 * WCAG 1.4.11 asks 3:1 for a control's icon, and this is a control (it
 * deletes a saved reflection). `--nw-ink-muted` is also the kit's own
 * convention for an icon at rest — `AccordionCard`'s chevron uses it.
 * Hover stays `--nw-alert`.
 */
export default function ReflectionList({ items, label, onEdit, onDelete, prefix, kit = false }) {
  if (!items.length) return null;
  return (
    <div
      className={kit ? "space-y-2 pt-3 border-t border-[var(--nw-line-faint)]" : "space-y-2 pt-3 border-t border-slate-100"}
      data-testid={`${prefix}-list`}
    >
      {items.map((r) => (
        <div
          key={r.reflection_id}
          className={kit ? "rounded-lg border border-[var(--nw-line)] p-3" : "rounded-lg border border-slate-200 p-3"}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className={kit ? "text-xs font-semibold text-[var(--nw-ink)]" : "text-xs font-semibold text-slate-900"}>
                {label(r)}
              </div>
              <p className={kit ? "text-sm text-[var(--nw-ink-lede)] mt-1 whitespace-pre-wrap break-words" : "text-sm text-slate-600 mt-1 whitespace-pre-wrap break-words"}>
                {r.body}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => onEdit(r)}
                className={kit ? "text-xs font-semibold text-[var(--nw-navy)] hover:underline" : "text-xs font-semibold text-[#0B2E59] hover:underline"}
                data-testid={`${prefix}-edit-${r.reflection_id}`}
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(r.reflection_id)}
                className={kit ? "text-[var(--nw-ink-muted)] hover:text-[var(--nw-alert)] transition" : "text-slate-400 hover:text-red-500 transition"}
                data-testid={`${prefix}-del-${r.reflection_id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
